import { Injectable } from '@angular/core';
import { Firestore, collectionData, collection, doc, query, where, orderBy, limit as firestoreLimit, getCountFromServer, getDoc, docData, deleteDoc } from '@angular/fire/firestore';
import { Observable, catchError, of, map } from 'rxjs';
import { Change } from '../models/change.interface';
import { Gig } from '../models/gig.interface';
import { Image } from '../models/image.interface';
import { User } from '../models/user.interface';

@Injectable({
  providedIn: 'root',
})
export class GigService {
  constructor(private firestore: Firestore) {}

  getGigList(uid: string): Observable<Gig[]> {
    const gigsRef = collection(this.firestore, 'ge_users', uid, 'gigs');
    return collectionData(gigsRef, { idField: 'id' }) as Observable<Gig[]>;
  }

  getChange(uid: string, gig: string, id: string): Observable<Change> {
    const changeDoc = doc(this.firestore, `/ge_users/${uid}/gigs/${gig}/changes/${id}`);
    return docData(changeDoc) as Observable<Change>;
  }

  getDetail(uid: string, id: string): Observable<Gig> {
    const gigDoc = doc(this.firestore, `/ge_users/${uid}/gigs/${id}`);
    return docData(gigDoc, { idField: 'id' }) as Observable<Gig>;
  }

  getGigGallery(uid: string, id: string): Observable<Image[]> {
    const imagesRef = collection(this.firestore, '/ge_users/' + uid + '/gigs/' + id + '/images/');
    const q = query(
      imagesRef,
      orderBy('index', 'desc'),
      firestoreLimit(5)
    );
    return collectionData(q, { idField: 'id' }) as Observable<Image[]>;
  }

  async countClassifiedGigs(classification: string, country: string): Promise<number> {
    const collectionRef = collection(this.firestore, 'ge_gigs');
    const q = query(collectionRef, 
      where('classification', 'array-contains', classification), 
      where('country', '==', country),
      where('visibility', '==', true)
    );
    const count = await getCountFromServer(q);
    return count.data().count;
  }

  async newClassifiedGigs(classification: string, country: string): Promise<number> {
    const month = Date.now() - 30 * 24 * 60 * 60 * 1000;
    const collectionRef = collection(this.firestore, 'ge_gigs');
    const q = query(collectionRef, 
      where('classification', 'array-contains', classification),
      where('country', '==', country),
      where('visibility', '==', true),
      where('publish', '>=', month)
    );
    const count = await getCountFromServer(q);
    return count.data().count;
  }

  async countGlobalGigs(classification: string): Promise<number> {
    const collectionRef = collection(this.firestore, 'ge_gigs');
    const q = query(collectionRef,
      where('classification', 'array-contains', classification),
      where('visibility', '==', true)
    );
    const count = await getCountFromServer(q);
    return count.data().count;
  }

  async newGlobalGigs(classification: string): Promise<number> {
    const month = Date.now() - 30 * 24 * 60 * 60 * 1000;
    const collectionRef = collection(this.firestore, 'ge_gigs');
    const q = query(collectionRef,
      where('classification', 'array-contains', classification),
      where('visibility', '==', true),
      where('publish', '>=', month)
    );
    const count = await getCountFromServer(q);
    return count.data().count;
  }

  async countIndustryGigs(grouping: string, country: string): Promise<number> {
    const collectionRef = collection(this.firestore, 'ge_gigs');
    const q = query(collectionRef,
      where('grouping', 'array-contains', grouping),
      where('country', '==', country),
      where('visibility', '==', true)
    );
    const count = await getCountFromServer(q);
    return count.data().count;
  }

  async countCountryGigs(country: string): Promise<number> {
    const collectionRef = collection(this.firestore, 'ge_gigs');
    const q = query(collectionRef,
      where('location', '==', 'onsite'),
      where('country', '==', country),
      where('visibility', '==', true)
    );
    const count = await getCountFromServer(q);
    return count.data().count;
  }

  async countRemoteGigs(): Promise<number> {
    const collectionRef = collection(this.firestore, 'ge_gigs');
    const q = query(collectionRef,
      where('location', '==', 'remote'),
      where('visibility', '==', true)
    );
    const count = await getCountFromServer(q);
    return count.data().count;
  }

  async countGigs(): Promise<number> {
    const collectionRef = collection(this.firestore, 'ge_gigs');
    const q = query(collectionRef, where('visibility', '==', true));
    const count = await getCountFromServer(q);
    return count.data().count;
  }

  getCountryGigs(country: string, limitValue: number): Observable<Gig[]> {
    const gigCollectionRef = collection(this.firestore, 'ge_gigs');
    const q = query(gigCollectionRef,
      where('country', '==', country),
      where('location', '==', 'onsite'),
      where('visibility', '==', true),
      orderBy('unpublish', 'desc'),
      firestoreLimit(limitValue)
    );
    return collectionData(q, { idField: 'id' }) as Observable<Gig[]>;
  }

  getLocalGigs(location: string, country: string, limitValue: number): Observable<Gig[]> {
    const gigCollectionRef = collection(this.firestore, 'ge_gigs');
    const q = query(gigCollectionRef,
      where('country', '==', country),
      where('cities', 'array-contains', location),
      where('visibility', '==', true),
      orderBy('unpublish', 'desc'),
      firestoreLimit(limitValue)
    );
    return collectionData(q, { idField: 'id' }) as Observable<Gig[]>;
  }

  getRegionGigs(location: string, country: string, limitValue: number): Observable<Gig[]> {
    const gigsRef = collection(this.firestore, 'ge_gigs');
    const q = query(gigsRef,
      where('country', '==', country),
      where('regions', 'array-contains', location),
      where('visibility', '==', true),
      orderBy('unpublish', 'desc'),
      firestoreLimit(limitValue)
    );
    return collectionData(q, { idField: 'id' }) as Observable<Gig[]>;
  }

  getClassifiedGigs(classification: string, country: string, limitValue: number): Observable<Gig[]> {
    const gigsRef = collection(this.firestore, 'ge_gigs');
    const q = query(gigsRef,
      where('classification', 'array-contains', classification),
      where('country', '==', country),
      where('visibility', '==', true),
      orderBy('unpublish', 'desc'),
      firestoreLimit(limitValue)
    );
    return collectionData(q, { idField: 'id' }) as Observable<Gig[]>;
  }

  getAllClassifiedGigs(classification: string, limitValue: number): Observable<Gig[]> {
    const gigCollectionRef = collection(this.firestore, 'ge_gigs');
    const q = query(gigCollectionRef,
      where('classification', 'array-contains', classification),
      where('visibility', '==', true),
      orderBy('unpublish', 'desc'),
      firestoreLimit(limitValue)
    );
    return collectionData(q, { idField: 'id' }) as Observable<Gig[]>;
  }

  getIndustryGigs(grouping: string, country: string, limitValue: number): Observable<Gig[]> {
    const gigsRef = collection(this.firestore, 'ge_gigs');
    const q = query(gigsRef,
      where('grouping', 'array-contains', grouping),
      where('country', '==', country),
      where('visibility', '==', true),
      orderBy('unpublish', 'desc'),
      firestoreLimit(limitValue)
    );
    return collectionData(q, { idField: 'id' }) as Observable<Gig[]>;
  }

  getAllIndustryGigs(grouping: string, limitValue: number): Observable<Gig[]> {
    const gigsRef = collection(this.firestore, 'ge_gigs');
    const q = query(gigsRef,
      where('grouping', 'array-contains', grouping),
      where('visibility', '==', true),
      orderBy('unpublish', 'desc'),
      firestoreLimit(limitValue)
    );
    return collectionData(q, { idField: 'id' }) as Observable<Gig[]>;
  }

  getRemoteGigs(limitValue: number) {
    const gigCollectionRef = collection(this.firestore, 'ge_gigs');
    const q = query(gigCollectionRef,
      where('location', '==', 'remote'),
      where('visibility', '==', true),
      orderBy('unpublish', 'desc'),
      firestoreLimit(limitValue)
    );
    return collectionData(q, { idField: 'id' }) as Observable<Gig[]>;
  }

  gigs() {
    const gigCollectionRef = collection(this.firestore, 'ge_gigs');
    const q = query(gigCollectionRef,
      where('visibility', '==', true),
      orderBy('unpublish', 'desc'),
      firestoreLimit(10)
    );
    return collectionData(q, { idField: 'id' }) as Observable<Gig[]>;
  }

  getProfile(uid: string) {
    const userRef = doc(this.firestore, `ge_users/${uid}`);
    return getDoc(userRef);
  }

  getProvider(uid: string) {
    const userRef = doc(this.firestore, `ge_users/${uid}`);
    return getDoc(userRef);
  }

  getCustomer(uid: string) {
    const userRef = doc(this.firestore, `ge_users/${uid}`);
    return getDoc(userRef);
  }

  gigCard(id: string) {
    const gigRef = doc(this.firestore, `ge_gigs/${id}`);
    return getDoc(gigRef);
  }

  gigDetail(id: string): Observable<Gig> {
    const gigDoc = doc(this.firestore, `ge_gigs/${id}`);
    return docData(gigDoc, { idField: 'id' }) as Observable<Gig>;
  }

  gigDetails(slug: string) {
    const gigsRef = collection(this.firestore, 'ge_gigs');
    const q = query(gigsRef, where('slug', '==', slug));
    return collectionData(q, { idField: 'id' }) as Observable<Gig[]>;
  }

  getGigs(): Observable<any[]> {
    const gigsCollectionRef = collection(this.firestore, 'gigs');
    return collectionData(gigsCollectionRef);
  }

  gigsters(id: string): Observable<User[]> {
    const gigstersRef = collection(this.firestore, `ge_gigs/${id}/gigsters`);
    const q = query(
      gigstersRef,
      orderBy('agree', 'desc'),
      orderBy('reject', 'desc'),
      orderBy('applied', 'desc'),
      // orderBy('rating', 'desc'),
      firestoreLimit(5)
    );
    return collectionData(q, { idField: 'id' }) as Observable<User[]>;
  }

  delete(uid: string, id: string) {
    const gigRef = doc(this.firestore, `ge_users/${uid}/gigs/${id}`);
    return deleteDoc(gigRef);
  }
}
